import React, { useEffect, useMemo, useState } from 'react';
import { App as PlaytableApp } from '@playtable/web';
import type { CardDefinition, CardLayout, FormatId, ImportedDeckPayload } from '#shared';
import type { Card } from '../App';
import { authService } from '../lib/supabase';
import { LegacyCouchSessionTab } from './LegacyCouchSessionTab';

export interface SessionTabProps {
  deck?: Card[];
  sideboard?: Card[];
  deckName?: string;
  format?: string;
}

interface DiscoverSessionHandoff {
  deckName?: string;
  cards?: Array<{
    name: string;
    quantity: number;
    imageUrl?: string;
    cmc?: number;
    isLand?: boolean;
    colors?: string[];
    type?: string;
    manaCost?: string;
    oracleText?: string;
    isCommander?: boolean;
  }>;
}

const VALID_LAYOUTS = new Set<CardLayout>([
  'normal',
  'transform',
  'modal_dfc',
  'split',
  'adventure',
  'flip',
  'meld',
]);

function normalizeFormat(raw?: string): FormatId {
  const f = (raw ?? '').trim().toLowerCase();
  if (f === 'commander' || f === 'edh') return 'commander';
  if (f === 'two-headed' || f === 'two_headed_giant' || f === '2hg') return 'two_headed_giant';
  if (f === 'brawl') return 'brawl';
  if (f === 'standard') return 'standard';
  if (f === 'modern') return 'modern';
  if (f === 'pioneer') return 'pioneer';
  if (f === 'legacy') return 'legacy';
  if (f === 'vintage') return 'vintage';
  if (f === 'pauper') return 'pauper';
  return 'commander';
}

function fallbackCardImageUrl(name: string, imageUrl?: string): string {
  if (imageUrl && imageUrl.trim().length > 0) return imageUrl;
  return `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(name)}&format=image&version=normal`;
}

function mapDeckstrCardToDefinition(card: Card): CardDefinition {
  const rawLayout = (card.layout ?? 'normal') as CardLayout;
  const layout: CardLayout = VALID_LAYOUTS.has(rawLayout) ? rawLayout : 'normal';
  const colors = Array.isArray(card.colorIdentity) && card.colorIdentity.length > 0
    ? card.colorIdentity
    : Array.isArray(card.colors)
    ? card.colors
    : [];

  return {
    scryfallId: card.scryfallId || card.id || `deckstr_${card.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
    name: card.name,
    manaCost: card.manaCost || '',
    cmc: Number.isFinite(card.cmc) ? card.cmc : 0,
    typeLine: card.type || (card.isLand ? 'Land' : 'Spell'),
    oracleText: card.oracleText || '',
    colors,
    imageUrl: fallbackCardImageUrl(card.name, card.imageUrl),
    backImageUrl: card.backImageUrl,
    backName: card.backName,
    backTypeLine: card.backType,
    layout,
  };
}

function buildPayloadFromDeckstrBuilder(
  deck: Card[],
  sideboard: Card[],
  deckName?: string,
  format?: string
): ImportedDeckPayload | null {
  if (deck.length === 0 && sideboard.length === 0) return null;

  const commanders: CardDefinition[] = [];
  const companions: CardDefinition[] = [];
  const mainboard: { card: CardDefinition; quantity: number }[] = [];
  const sideEntries: { card: CardDefinition; quantity: number }[] = [];

  for (const item of deck) {
    if (item.isExtraCard) continue;
    const qty = Math.max(0, Math.floor(item.quantity ?? 1));
    if (qty <= 0) continue;
    const def = mapDeckstrCardToDefinition(item);

    if (item.isCommander) {
      commanders.push(def);
      if (qty > 1) {
        mainboard.push({ card: def, quantity: qty - 1 });
      }
    } else if (item.isCompanion) {
      companions.push(def);
    } else if (item.isSideboard) {
      sideEntries.push({ card: def, quantity: qty });
    } else {
      mainboard.push({ card: def, quantity: qty });
    }
  }

  for (const sb of sideboard) {
    const qty = Math.max(0, Math.floor(sb.quantity ?? 1));
    if (qty <= 0) continue;
    const def = mapDeckstrCardToDefinition(sb);
    if (sb.isCompanion) {
      companions.push(def);
    } else {
      sideEntries.push({ card: def, quantity: qty });
    }
  }

  const totalCount =
    commanders.length +
    mainboard.reduce((acc, c) => acc + c.quantity, 0);
  const inferredFormat =
    format && format.trim().length > 0
      ? normalizeFormat(format)
      : commanders.length > 0 || totalCount >= 98
      ? 'commander'
      : 'standard';

  return {
    name: deckName?.trim() || commanders[0]?.name || 'Deckstr Builder Deck',
    format: inferredFormat,
    commanders,
    companions: companions.length > 0 ? companions : undefined,
    mainboard,
    sideboard: sideEntries,
    maybeboard: [],
  };
}

function buildPayloadFromDiscoverHandoff(handoff: DiscoverSessionHandoff): ImportedDeckPayload | null {
  const cards = handoff.cards ?? [];
  if (cards.length === 0) return null;

  const commanders: CardDefinition[] = [];
  const mainboard: { card: CardDefinition; quantity: number }[] = [];

  for (const c of cards) {
    const qty = Math.max(0, Math.floor(c.quantity ?? 1));
    if (qty <= 0 || !c.name) continue;
    const def: CardDefinition = {
      scryfallId: `discover_${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
      name: c.name,
      manaCost: c.manaCost ?? '',
      cmc: Number.isFinite(c.cmc) ? Number(c.cmc) : 0,
      typeLine: c.type || (c.isLand ? 'Land' : 'Spell'),
      oracleText: c.oracleText ?? '',
      colors: Array.isArray(c.colors) ? c.colors : [],
      imageUrl: fallbackCardImageUrl(c.name, c.imageUrl),
      layout: 'normal',
    };
    if (c.isCommander) {
      commanders.push(def);
    } else {
      mainboard.push({ card: def, quantity: qty });
    }
  }

  const totalCount =
    commanders.length + mainboard.reduce((acc, e) => acc + e.quantity, 0);

  return {
    name: handoff.deckName?.trim() || 'Discover Deck',
    format: commanders.length > 0 || totalCount >= 98 ? 'commander' : 'standard',
    commanders,
    mainboard,
    sideboard: [],
    maybeboard: [],
  };
}

export function SessionTab({
  deck = [],
  sideboard = [],
  deckName,
  format,
}: SessionTabProps) {
  const [mode, setMode] = useState<'playtable' | 'couch'>('playtable');
  const [userHandle, setUserHandle] = useState<string | null>(null);
  const [discoverPayload, setDiscoverPayload] = useState<ImportedDeckPayload | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('deckstr_session_deck');
      if (raw) {
        localStorage.removeItem('deckstr_session_deck');
        const parsed = JSON.parse(raw) as DiscoverSessionHandoff;
        const built = buildPayloadFromDiscoverHandoff(parsed);
        if (built) {
          setDiscoverPayload(built);
        }
      }
    } catch {
      // ignore malformed localStorage handoff
    }
  }, []);

  useEffect(() => {
    let active = true;
    authService
      .getCurrentUser()
      .then((user) => {
        if (!active || !user) return;
        const handle =
          (user.user_metadata?.username as string | undefined) ||
          (user.user_metadata?.display_name as string | undefined) ||
          user.email?.split('@')[0] ||
          null;
        setUserHandle(handle);
      })
      .catch(() => {});

    const sub = authService.onAuthStateChange((_event, session) => {
      const u = session?.user;
      if (!u) {
        setUserHandle(null);
        return;
      }
      const handle =
        (u.user_metadata?.username as string | undefined) ||
        (u.user_metadata?.display_name as string | undefined) ||
        u.email?.split('@')[0] ||
        null;
      setUserHandle(handle);
    });

    return () => {
      active = false;
      sub.data.subscription.unsubscribe();
    };
  }, []);

  const builderPayload = useMemo(
    () => buildPayloadFromDeckstrBuilder(deck, sideboard, deckName, format),
    [deck, sideboard, deckName, format]
  );

  const activeDeckPayload = discoverPayload ?? builderPayload;

  if (mode === 'couch') {
    return <LegacyCouchSessionTab onSwitchToPlaytable={() => setMode('playtable')} />;
  }

  return (
    <div className="h-full overflow-y-auto rounded-xl border border-slate-800/80 bg-[#070a12]">
      <PlaytableApp
        embeddedInDeckstr
        deckstrDeck={activeDeckPayload}
        deckstrUserHandle={userHandle}
        autoGoldfishOnMount={discoverPayload !== null}
        onSwitchToLocalCouch={() => setMode('couch')}
      />
    </div>
  );
}

export default SessionTab;
